import { Box } from '@mui/material'
import { useTabsController } from 'hooks/useTabsController'
import PageContainer from 'layouts/PageContainer'
import { ICowLightInfo } from 'models/ICattlesData'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import CattlesApiService from 'services/cattlesApiServices'
import { addCowStatuses } from 'store/cattlesSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import TabsFilter from 'ui/breadCrumbs/TabsFilter'
import Preloader from 'ui/Preloader'
import CowMainInfo from './ui/CowMainInfo'
import HealthCard from './ui/HealthCard'
import WidjetsInPasportPage from './ui/WidjetsInPasportPage'
import YieldQtyCard from './ui/YieldQtyCard'

enum TABS_KEYS {
	MAIN_INFORMATION = 'main_information',
	HEALTH = 'health',
	MILK_YIELD = 'milk_yield',
}

const CowPasportPage = () => {
	const { tab, changeTab } = useTabsController<TABS_KEYS>({
		keyName: 'tab',
		defaultValue: TABS_KEYS.MAIN_INFORMATION,
	})
	const { permissions } = useAppSelector(state => state.authReducer)
	const tabOptions = [
		{
			key: TABS_KEYS.MAIN_INFORMATION,
			title: 'Общая информация',
		},
		...(permissions.license_limpness || permissions.license_fatness
			? [
					{
						key: TABS_KEYS.HEALTH,
						title: 'Здоровье',
					},
				]
			: []),
		...(permissions?.license_identification
			? [
					{
						key: TABS_KEYS.MILK_YIELD,
						title: 'Надои',
					},
				]
			: []),
	]
	const { tagNumber } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const cowNavState = location.state as {
		identified?: boolean
		cowId?: number
	} | null
	const initialCowNavStateRef = useRef(cowNavState)
	const [cowInfo, setCowInfo] = useState<ICowLightInfo>()
	const dispatch = useAppDispatch()

	useLayoutEffect(() => {
		if (!tagNumber) return

		setCowInfo(undefined)
		dispatch(
			addCowStatuses({
				yieldStatus: undefined,
				treatmentStatus: undefined,
				fatnessStatus: undefined,
				identified: initialCowNavStateRef.current?.identified,
			}),
		)
	}, [dispatch, tagNumber])

	useEffect(() => {
		if (!tagNumber) return

		let isCancelled = false
		const identifiedFromNav = initialCowNavStateRef.current?.identified
		const cowIdFromNav = initialCowNavStateRef.current?.cowId

		const loadCowData = async () => {
			try {
				let cowId = cowIdFromNav
				let identifiedByTag: boolean | undefined

				if (!cowId) {
					const { data: cowByTag } =
						await CattlesApiService.getCowInfoByTagId(tagNumber)
					cowId = cowByTag.id
					identifiedByTag = cowByTag.identified
				}

				if (!cowId || isCancelled) return

				const { data } = await CattlesApiService.getCowLightInfoById(cowId)

				if (isCancelled) return

				const resolvedIdentified =
					identifiedFromNav ?? identifiedByTag ?? data.identified

				setCowInfo({ ...data, identified: resolvedIdentified })
				dispatch(
					addCowStatuses({
						yieldStatus: data.yield_status,
						treatmentStatus: data.treatment_status,
						fatnessStatus: data.fatness_status,
						identified: resolvedIdentified,
					}),
				)
			} catch {
				if (!isCancelled) {
					navigate('/cattles')
				}
			}
		}

		void loadCowData()

		return () => {
			isCancelled = true
		}
	}, [dispatch, navigate, tagNumber])

	if (!cowInfo)
		return (
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					flex: 1,
					height: '80vh',
				}}
			>
				<Preloader />
			</Box>
		)

	const renderContent = (currentTab: TABS_KEYS) => {
		switch (currentTab) {
			case TABS_KEYS.MAIN_INFORMATION:
				return (
					<>
						<CowMainInfo cattleInfo={cowInfo} />
						{(permissions?.license_fatness ||
							permissions?.license_identification ||
							permissions?.license_limpness) && (
							<WidjetsInPasportPage cattleInfo={cowInfo} />
						)}
					</>
				)

			case TABS_KEYS.HEALTH:
				return (
					<>
						{(permissions?.license_fatness ||
							permissions?.license_limpness) && (
							<HealthCard
								cowId={cowInfo.id}
								lactationLength={cowInfo.lactation_length}
							/>
						)}
					</>
				)

			case TABS_KEYS.MILK_YIELD:
				return (
					<>
						{permissions?.license_identification && (
							<YieldQtyCard cowId={cowInfo.id} cowInfo={cowInfo} />
						)}
					</>
				)
		}
	}
	return (
		<PageContainer>
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					backgroundColor: COLORS.background,
					zIndex: 90,
				}}
			>
				<TabsFilter
					availableTabs={tabOptions}
					onClick={key => changeTab(key as TABS_KEYS)}
					selectedKey={tab}
				/>
			</Box>
			{renderContent(tab)}
			<Box sx={{ paddingBottom: '500px' }} />
		</PageContainer>
	)
}

export default CowPasportPage
